import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

import { examples } from './examples';
import RFviewCanvas from './RFviewCanvas';
import AutoResizeTextarea from './AutoResizeTextarea';

// Delay (ms) before the image popup opens on hover, so the native config shows first.
const IMAGE_HOVER_DELAY = 500;

function formatMetainfo(raw) {
  const parts = raw.split(' ');
  let text = '';
  text += `structure*reactivity score = ${parts[1]}\n`;
  text += `structure score = ${parts[2]}\n`;
  text += `reactivity score = ${parts[3]}\n`;
  text += `configs = ${parts[4]}`;
  if (parts.length >= 6) text += `\nmetrics = ${parts[5]}`;
  return text;
}

// Main component (form + result)
function SquarnaApp() {
  const [rnaSequence, setRnaSequence] = useState('');
  const [restraints, setRestraints] = useState('');
  const [reference, setReference] = useState('');
  const [reactivities, setReactivities] = useState('');
  const [alignment, setAlignment] = useState('');
  
  const [predictionData, setPredictionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [queuePosition, setQueuePosition] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  const [currentExampleId, setCurrentExampleId] = useState(null);
  const [showRestraints, setShowRestraints] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const [showReactivities, setShowReactivities] = useState(false);
  const [showAlignment, setShowAlignment] = useState(false);
  const [reactivitiesFilename, setReactivitiesFilename] = useState('');
  const [alignmentFilename, setAlignmentFilename] = useState('');
  const [showParameters, setShowParameters] = useState(false);
  const [vizTool, setVizTool] = useState('rfview');
  const [structuresNumber, setStructuresNumber] = useState(5);
  const [showConsensus, setShowConsensus] = useState(false);
  const [consensusLimit, setConsensusLimit] = useState(1);
  const [displayedConsensusLimit, setDisplayedConsensusLimit] = useState(1);
  const [rfamEnabled, setRfamEnabled] = useState(true);
  const [g4Enabled, setg4Enabled] = useState(true);
  const [rbpEnabled, setRbpEnabled] = useState(true);
  
  // Config + image are managed per structure row, keyed by `key` (prediction index | 'consensus')
  // Cashing results by key 
  const [blocks, setBlocks] = useState({});

  const svgCacheRef = useRef({});
  const blocksRef = useRef({});
  const openTimers = useRef({});
  const closeTimers = useRef({});
  const svgReqRef = useRef({});
  const rfviewRef = useRef(null);

  const reactivitiesFileInputRef = useRef(null);
  const alignmentFileInputRef = useRef(null);
  const { jobId: urlJobId } = useParams();

  useEffect(() => {
    if (urlJobId) {
      setCurrentJobId(urlJobId);
      checkJobStatus(urlJobId);
    }
  }, [urlJobId]);

  // Check job status 
  const checkJobStatus = async (jobId) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}/`);
      if (!response.ok) {
        throw new Error('Job not found');
      }
      const data = await response.json();

      setRnaSequence(data.rna_sequence_raw);
      setRestraints(data.restraints_raw);
      setReference(data.reference_raw);
      setReactivities(data.reactivities_raw);
      setAlignment(data.alignment_raw);
      setStructuresNumber(data.tl);
      setConsensusLimit(data.cl);
      setDisplayedConsensusLimit(data.cl);
      setRfamEnabled(data.rfam);
      setg4Enabled(data.g4);
      setRbpEnabled(data.rbp);
      setShowConsensus(data.show_consensus);
    
      if (data.restraints && data.restraints.trim()) setShowRestraints(true);
      if (data.reference && data.reference.trim()) setShowReference(true);
      if (data.reactivities && data.reactivities.trim()) setShowReactivities(true);
      if (data.alignment && data.alignment.trim()) setShowAlignment(true);
    
      if (data.reactivities_filename) setReactivitiesFilename(data.reactivities_filename);
      if (data.alignment_filename) setAlignmentFilename(data.alignment_filename);
      
      setJobStatus(data.status);

      if (data.status === 'completed') {
        setPredictionData(data);
        if (data.warnings && data.warnings.length > 0) {
          setWarnings(data.warnings);
        }
        setLoading(false);
      } else if (data.status === 'failed') {
        setError(data.error || 'Prediction failed');
        setLoading(false);
      } else if (data.status === 'running' || data.status === 'queued') {
        setTimeout(() => checkJobStatus(jobId), 3000);
        setLoading(true);
      }
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError({ error:'Under maintenance. Try again later. Sorry!' });
      } else {
        setError({ error: err.message });
      }
      setLoading(false);
    }
  };

  const ExampleSelector = ({ examples, onSelectExample, currentExampleId }) => {
    const [hoveredId, setHoveredId] = useState(null);
    return (
      <div className="examples-section">
        <div className="examples-grid">
          {examples.map(example => (
            <div
              key={example.id}
              className="example-button-wrapper"
              onMouseEnter={() => setHoveredId(example.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <button
                className={`example-button ${currentExampleId === example.id ? 'active' : ''}`}
                onClick={() => onSelectExample(example)}
                title={example.description}
              >
                {example.name}
              </button>
              {hoveredId === example.id && (
                <a
                  href={example.helpUrl || '/help'}
                  className="example-help-link"
                  title="Help"
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  ?
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };
  

  const resetForm = () => {
    window.history.pushState({}, '', '/');

    setRnaSequence('');
    setRestraints('');
    setReference('');
    setReactivities('');
    setAlignment('');
    setStructuresNumber(5);
    setConsensusLimit(1);
    setRfamEnabled(true);
    setg4Enabled(true);
    setRbpEnabled(true);
    setShowConsensus(false);

    setShowRestraints(false);
    setShowReference(false);
    setShowReactivities(false);
    setShowAlignment(false);
    setShowParameters(false);
    setCurrentExampleId(null);
    setReactivitiesFilename('');
    setAlignmentFilename('');

    setPredictionData(null);
    setBlocks({});
    setError(null);
    setWarnings([]);
    setQueuePosition(null);
    setCurrentJobId(null);
    setJobStatus(null);

    window.dispatchEvent(new Event('popstate'));

  };

  const loadExample = (example) => {
    if (currentExampleId === example.id) {
      resetForm();
      setCurrentExampleId(null);
    } else {
      setRnaSequence(example.data.rna_sequence);
      setRestraints(example.data.restraints);
      setReference(example.data.reference);
      setReactivities(example.data.reactivities);
      setAlignment(example.data.alignment);
      setStructuresNumber(example.data.parameters.tl);
      setConsensusLimit(example.data.parameters.cl);
      setRfamEnabled(example.data.parameters.rfam);
      setg4Enabled(example.data.parameters.g4);
      setRbpEnabled(example.data.parameters.rbp);
      setShowConsensus(!!example.data.parameters.show_consensus);
      setCurrentExampleId(example.id);

      setShowReactivities(!!example.data.reactivities);
      setShowRestraints(!!example.data.restraints);
      setShowReference(!!example.data.reference);
      setShowAlignment(!!example.data.alignment);
      setShowParameters(!!example.data.parameters.showParameters);

      setPredictionData(null);
      setBlocks({});
      setError(null);
      setWarnings([]);
      setQueuePosition(null);
      setCurrentJobId(null);
      setJobStatus(null);
    }
  };

  const AdditionalInputsButtons = ({ 
    showReactivities, setShowReactivities,
    showRestraints, setShowRestraints,
    showReference, setShowReference,
    showAlignment, setShowAlignment,
    showParameters, setShowParameters
  }) => {
    return (
      <div className="additional-inputs-buttons">
        <button
          type="button"
          className={`toggle-button ${showReactivities ? 'active' : ''}`}
          onClick={() => setShowReactivities(!showReactivities)}
          title="Add residue reactivities (e.g., from chemical probing experiments)"
        >
          {showReactivities ? '- Reactivities' : '+ Reactivities'}
        </button>
        <button
          type="button"
          className={`toggle-button ${showRestraints ? 'active' : ''}`}
          onClick={() => setShowRestraints(!showRestraints)}
          title="Add structural restraints"
        >
          {showRestraints ? '- Restraints' : '+ Restraints'}
        </button>
        <button
          type="button"
          className={`toggle-button ${showReference ? 'active' : ''}`}
          onClick={() => setShowReference(!showReference)}
          title="Add a reference 2D structure"
        >
          {showReference ? '- Reference' : '+ Reference'}
        </button>
        <button
          type="button"
          className={`toggle-button ${showAlignment ? 'active' : ''}`}
          onClick={() => setShowAlignment(!showAlignment)}
          title="Add a sequences alignment"
        >
          {showAlignment ? '- Alignment' : '+ Alignment'}
        </button>
        <button
          type="button"
          className={`toggle-button ${showParameters ? 'active' : ''}`}
          onClick={() => setShowParameters(!showParameters)}
          title="Adjust parameters"
        >
          {showParameters ? '- Parameters' : '+ Parameters'}
        </button>
      </div>
    );
  };

  const handleReactivitiesFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setReactivities(text.trim());
      setReactivitiesFilename(file.name);
    };
    reader.onerror = () => {
      setError('Failed to read reactivities file.');
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleClearReactivitiesFile = () => {
    setReactivitiesFilename('');
    setReactivities('');
    if (reactivitiesFileInputRef.current) {
      reactivitiesFileInputRef.current.value = '';
    }
  };

  const handleAlignmentFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setAlignment(text.trim());
      setAlignmentFilename(file.name);
    };
    reader.onerror = () => {
      setError('Failed to read alignment file.');
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleClearAlignmentFile = () => {
    setAlignmentFilename('');
    setAlignment('');
    if (alignmentFileInputRef.current) {
      alignmentFileInputRef.current.value = '';
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setLoading(true);
    setError(null);
    setWarnings([]);
    setPredictionData(null);
    setBlocks({});
    setQueuePosition(null);
    setCurrentJobId(null);
    setJobStatus(null);

    const requestBody = {
      rna_sequence: rnaSequence,
      tl: structuresNumber,
      cl: consensusLimit,
      rfam: rfamEnabled,
      g4: g4Enabled,
      rbp: rbpEnabled,
      show_consensus: showConsensus,
      restraints: restraints,
      reference: reference,
      reactivities: reactivities,
      alignment: alignment,
    };

    try {
      const response = await fetch(`/api/jobs/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // IP limit
        if (response.status === 429 && errorData.your_active_jobs) {
          let errorMessage = errorData.error;
          
          if (errorData.oldest_job_id) {
            errorMessage += `\n\nOldest job: ${errorData.oldest_job_id},`;
            errorMessage += `\ncreated: ${new Date(errorData.oldest_job_created).toLocaleString()}.`;
            
            if (errorData.estimated_wait_minutes) {
              errorMessage += `\nEstimated wait: ~${errorData.estimated_wait_minutes} minutes`;
            }
          }
          
          throw new Error(errorMessage);
        }
        
        // Usual queue error 
        if (response.status === 429) {
          setQueuePosition(errorData.queue_position);
          throw new Error(errorData.error || 'Server busy');
        }
        
        setError(errorData);
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      // URL without form parameters 
      const jobUrl = `/jobs/${data.job_id}`;
      // Don't open new page 
      window.history.pushState({}, '', jobUrl);

      setCurrentJobId(data.job_id);
      setError(null);
      setQueuePosition(null);

      checkJobStatus(data.job_id);

  } catch (err) {
    if (err.message === 'Failed to fetch') {
      setError({ error:'Under maintenance. Try again later. Sorry!' });
    } else {
      setError({ error: err.message });
    }
  } finally {
    setLoading(false);
  }
};
  
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);

  // Per-key block helpers 
  const upsertBlock = (key, fields) =>
    setBlocks((prev) => ({ ...prev, [key]: { ...prev[key], ...fields } }));
  const patchBlockIfPresent = (key, patch) =>
    setBlocks((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], ...patch } } : prev));
  const removeBlock = (key) =>
    setBlocks((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  // Resolve the SVG for a block (cached per sequence+structure; lazy import).
  const ensureSvg = (key, structure) => {
    const seq = (predictionData && predictionData.rna_sequence) || '';
    const cacheKey = `${seq} ${structure}`;
    const cache = svgCacheRef.current;
    if (Object.prototype.hasOwnProperty.call(cache, cacheKey)) {
      patchBlockIfPresent(key, { svg: cache[cacheKey] });
      return;
    }
    upsertBlock(key, { svg: undefined }); 
    const reqId = (svgReqRef.current[key] = (svgReqRef.current[key] || 0) + 1);
    import('./rnaImage')
      .then(({ renderStructureSVG }) => renderStructureSVG(seq, structure))
      .then((svg) => {
        cache[cacheKey] = svg;
        if (svgReqRef.current[key] === reqId) patchBlockIfPresent(key, { svg });
      })
      .catch(() => {
        cache[cacheKey] = null;
        if (svgReqRef.current[key] === reqId) patchBlockIfPresent(key, { svg: null });
      });
  };

  const clearOpenTimer = (key) => {
    if (openTimers.current[key]) { clearTimeout(openTimers.current[key]); openTimers.current[key] = null; }
  };
  const clearCloseTimer = (key) => {
    if (closeTimers.current[key]) { clearTimeout(closeTimers.current[key]); closeTimers.current[key] = null; }
  };

  // Hover a "?": after a delay, open a transient (non-pinned) preview
  const handleQHover = (structure, metainfo, key) => {
    if (blocksRef.current[key] && blocksRef.current[key].pinned) return;
    clearCloseTimer(key);
    clearOpenTimer(key);
    openTimers.current[key] = setTimeout(() => {
      upsertBlock(key, { structure, pinned: false, metainfo });
      if (vizTool !== 'rfview') ensureSvg(key, structure);
    }, IMAGE_HOVER_DELAY);
  };

  // Leave the "?": cancel a pending open; close the preview unless pinned.
  const handleQLeave = (key) => {
    clearOpenTimer(key);
    const b = blocksRef.current[key];
    if (b && !b.pinned) {
      closeTimers.current[key] = setTimeout(() => removeBlock(key), 150);
    }
  };

  const switchRfviewCanvas = (key) => {
    if (!rfviewRef.current) return;
    const canvasIdx = key === 'consensus'
      ? (predictionData ? predictionData.predictions.length : 0)
      : key;
    rfviewRef.current.switchTo(canvasIdx);
  };

  // Click a "?": toggle the pinned block for this row. Clicking the same "?" again closes it
  const handleQClick = (structure, metainfo, key) => {
    clearOpenTimer(key);
    clearCloseTimer(key);
    if (vizTool === 'rfview') switchRfviewCanvas(key);
    const cur = blocksRef.current[key];
    if (cur && cur.pinned) {
      removeBlock(key);
      return;
    }
    upsertBlock(key, { structure, pinned: true, metainfo });
    if (vizTool !== 'rfview') ensureSvg(key, structure);
  };

  // Mouse over leave the image block itself 
  const handleBlockEnter = (key) => clearCloseTimer(key);
  const handleBlockLeave = (key) => {
    const b = blocksRef.current[key];
    if (b && !b.pinned) {
      closeTimers.current[key] = setTimeout(() => removeBlock(key), 150);
    }
  };

  // Renders the inline image block for a given row key 
  const renderImageBlock = (key) => {
    const b = blocks[key];
    if (!b) return null;
    const label = key === 'consensus' ? 'consensus' : Number(key) + 1;
    return (
      <div
        className={`rna-image-inline ${b.pinned ? 'pinned' : ''}`}
        onMouseEnter={() => handleBlockEnter(key)}
        onMouseLeave={() => handleBlockLeave(key)}
      >
        {b.svg === undefined ? (
          <div className="rna-image-placeholder">Rendering…</div>
        ) : b.svg ? (
          <>
            <div className="rna-image-svg" dangerouslySetInnerHTML={{ __html: b.svg }} />
            {b.pinned && (
              <button
                type="button"
                className="rna-download-button"
                onClick={() =>
                  import('./rnaImage').then(({ downloadSvg }) =>
                    downloadSvg(b.svg, `rna_structure_${label}.svg`)
                  )
                }
              >
                Download SVG
              </button>
            )}
          </>
        ) : (
          <div className="rna-image-placeholder">Preview unavailable</div>
        )}
      </div>
    );
  };

  return (
    <div className="App">
      <div className="container">
        {(currentJobId || urlJobId) && (
          <div className="job-info-bar">
            <div className="job-id-display">
              <strong>Job ID:</strong> {currentJobId || urlJobId}
            </div>

            {jobStatus && (
              <div className="job-status-display">
                <strong>Status:</strong> 
                <span className={`job-status job-status-${jobStatus}`}>
                  {jobStatus}
                </span>

		{jobStatus === 'running' && (
		  <span className="inline-spinner"></span>
		)}
              </div>
            )}

            {queuePosition !== null && queuePosition > 0 && (
              <div className="queue-position-display">
                <strong>Queue position:</strong> {queuePosition}
              </div>
            )}
          </div>
        )}

        <div className="content-with-sidebar">
          <div className="content-main">
            <ExampleSelector
              examples={examples}
              onSelectExample={loadExample}
              currentExampleId={currentExampleId}
            />

            <form onSubmit={handleSubmit}>
              <div className="main-input-section">
                <div className="panel-header-row">
                  <label 
                    htmlFor="rna_sequence"
                    title="RNA sequence up to 500 nts, in FASTA format or as plain text. Symbols 'AaCcGgUu' will be treated as the four types of RNA bases in the case-insensitive manner. Symbols 'Tt' will be replaced with 'U'. Symbols ';&' will be treated as the separators of different RNA chains. Symbols '.-~' will be treated as gaps. Spacing symbols will be removed. All the other symbols will be treated as bases that cannot form base pairs."
                  >
                    RNA sequence:
                  </label>
                  <a
                    href="/help#rna-sequence"
                    className="panel-help-link"
                    title="Help"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ?
                  </a>
                </div>
                <textarea
                  id="rna_sequence"
                  rows="10"
                  placeholder="GGGGGCCACAGCAGAAGCGUUCACGUCGCAGCCCCUGUCAGCCAUUGCACUCCGGCUGCGAAUUCUGCU"
                  required
                  value={rnaSequence}
                  onChange={(e) => setRnaSequence(e.target.value)}
                  disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                  title="RNA sequence up to 500 nts, in FASTA format or as plain text. Symbols 'AaCcGgUu' will be treated as the four types of RNA bases in the case-insensitive manner. Symbols 'Tt' will be replaced with 'U'. Symbols ';&' will be treated as the separators of different RNA chains. Symbols '.-~' will be treated as gaps. Spacing symbols will be removed. All the other symbols will be treated as bases that cannot form base pairs."
                ></textarea>
              </div>
          
              {showReactivities && (
                <div className="input-panel">
                  <div className="panel-header-row">
                    <h3
                      className="panel-header"
                      title="Residue reactivities either as a space/tab separated list of float values (with the number of values equal to the sequence length, including the separator positions whose values will be ignored) or as a two-column table of space/tab separated value pairs, where the first is a 1-based position index and the second is its reactivity value (missing values will be filled with -999). Values of -10 and lower will be treated as missing values. Other float values will be clipped to the 0.0-1.0 range using min(max(x,0),1)."
                    >
                      Reactivities
                    </h3>
                    <a
                      href="/help#reactivities"
                      className="panel-help-link"
                      title="Help"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ?
                    </a>
                  </div>
                  <AutoResizeTextarea
                    id="reactivities"
                    rows={1}
                    maxHeight={300}
                    placeholder="1.118	0.764	0.822	0.867	0.08	-0.114	-0.126"
                    value={reactivities}
                    onChange={(e) => setReactivities(e.target.value)}
                    disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                    title="Residue reactivities either as a space/tab separated list of float values (with the number of values equal to the sequence length, including the separator positions whose values will be ignored) or as a two-column table of space/tab separated value pairs, where the first is a 1-based position index and the second is its reactivity value (missing values will be filled with -999). Values of -10 and lower will be treated as missing values. Other float values will be clipped to the 0.0-1.0 range using min(max(x,0),1)."
                  />
                  <div className="file-input-row">
                    <label
                      className="file-btn"
                      title="Upload as a file instead"
                      onClick={(e) => {
                        if (reactivitiesFilename) {
                          e.preventDefault();
                          handleClearReactivitiesFile();
                        }
                      }}
                    >
                      {reactivitiesFilename ? 'Clear' : 'Choose file'}
                      <input
                        ref={reactivitiesFileInputRef}
                        type="file"
                        onChange={handleReactivitiesFileChange}
                        className="hidden-file-input"
                        disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                      />
                    </label>
                    <span className="filename-text">
                      {reactivitiesFilename || 'No file chosen'}
                    </span>
                  </div>
                </div>
              )}

              {showRestraints && (
                <div className="input-panel">
                  <div className="panel-header-row">
                    <h3
                      className="panel-header"
                      title="Restraints line matching the sequence length. All pairs of brackets, (), [], {}, <>, and pairs of Latin letters (Aa,Bb,...Yy,Zz) will be treated as base pairs. Symbols '_' and ‘+’ will be treated as unpaired bases. The slash sign '/' will be treated as the base that cannot form any base pairs 'to the left' (i.e. with the counterpart being closer to the 5'-end). In contrast, the backslash sign '\' will be treated as the base that cannot form any base pairs 'to the right' (i.e. with the counterpart being closer to the 3'-end). All the other symbols will be treated as unrestrained positions."
                    >
                      Restraints
                    </h3>
                    <a
                      href="/help#restraints"
                      className="panel-help-link"
                      title="Help"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ?
                    </a>
                  </div>
                  <AutoResizeTextarea
                    id="restraints"
                    rows={1}
                    maxHeight={300}
                    placeholder="[[[[[[........................]]]]]]................................."
                    value={restraints}
                    onChange={(e) => setRestraints(e.target.value)}
                    disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                    title="Restraints line matching the sequence length. All pairs of brackets, (), [], {}, <>, and pairs of Latin letters (Aa,Bb,...Yy,Zz) will be treated as base pairs. Symbols '_' and ‘+’ will be treated as unpaired bases. The slash sign '/' will be treated as the base that cannot form any base pairs 'to the left' (i.e. with the counterpart being closer to the 5'-end). In contrast, the backslash sign '\' will be treated as the base that cannot form any base pairs 'to the right' (i.e. with the counterpart being closer to the 3'-end). All the other symbols will be treated as unrestrained positions."
                  />
                </div>
              )}

              {showReference && (
                <div className="input-panel">
                  <div className="panel-header-row">
                    <h3
                      className="panel-header"
                      title="Reference 2D structure in dot-bracket format, matching the sequence length. Pairs of brackets, (),[],{},<>, and pairs of Latin letters (Aa,Bb,...Yy,Zz) will be treated as base pairs."
                    >
                      Reference
                    </h3>
                    <a
                      href="/help#reference"
                      className="panel-help-link"
                      title="Help"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ?
                    </a>
                  </div>
                  <AutoResizeTextarea
                    id="reference"
                    rows={1}
                    maxHeight={300}
                    placeholder="[[[[[[...((((((((((.......))).]]]]]]..(((((..........)))))....)))))))"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                    title="Reference 2D structure in dot-bracket format, matching the sequence length. Pairs of brackets, (),[],{},<>, and pairs of Latin letters (Aa,Bb,...Yy,Zz) will be treated as base pairs."
                  />
                </div>
              )}

              {showAlignment && (
                <div className="input-panel">
                  <div className="panel-header-row">
                    <h3
                      className="panel-header"
                      title="Multiple sequence alignment in FASTA, Stockholm, or Clustal (.aln) format. The alignment must feature the input sequence, possibly with gaps."
                    >
                      Alignment
                    </h3>
                    <a
                      href="/help#alignment"
                      className="panel-help-link"
                      title="Help"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ?
                    </a>
                  </div>
                  <AutoResizeTextarea
                    id="alignment"
                    rows={3}
                    maxHeight={400}
                    placeholder={`>sequence_id
AUGGCCGGCAUGGUC-CCA--------GCCUCCUCGCUGGCGCCGGCUGGGCAACAUUCCGAA-GGGGA-CCG--U---CC-C----UC--GGUAAUGGCGAA---UGGGACCCA`}
                    value={alignment}
                    onChange={(e) => setAlignment(e.target.value)}
                    disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                    title="Multiple sequence alignment in FASTA, Stockholm, or Clustal (.aln) format. The alignment must feature the input sequence, possibly with gaps."
                  />
                  <div className="file-input-row">
                    <label
                      className="file-btn"
                      title="Upload as a file instead"
                      onClick={(e) => {
                        if (alignmentFilename) {
                          e.preventDefault();
                          handleClearAlignmentFile();
                        }
                      }}
                    >
                      {alignmentFilename ? 'Clear' : 'Choose file'}
                      <input
                        ref={alignmentFileInputRef}
                        type="file"
                        onChange={handleAlignmentFileChange}
                        className="hidden-file-input"
                        disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                      />
                    </label>
                    <span className="filename-text">
                      {alignmentFilename || 'No file chosen'}
                    </span>
                  </div>
                </div>
              )}

              {showParameters && (
                <div className="input-panel">
                  <div className="panel-header-row">
                    <h3 className="panel-header">Parameters</h3>
                    <a
                      href="/help#parameters"
                      className="panel-help-link"
                      title="Help"
                      target="_blank"
                      rel="noopener noreferrer"
                     >
                       ?
                     </a>
                  </div>
                  <div className="parameters-panel">
                    <div className="structures-number-display">
                      <label 
                        htmlFor="structures_number"
                        title="Number of predicted structures to report"
                      > 
                        # Structures:
                      </label>
                      <input
                        type="number"
                        id="structures_number"
                        value={structuresNumber}
                        onChange={(e) => setStructuresNumber(parseInt(e.target.value) || 0)}
                        min="1"
                        step="1"
                        disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                      />
                    </div>
                    <div className="consensus-display">
                      <label 
                        htmlFor="consensus_limit"
                        title="X for the consensus of the top-X structures"
                      >
                        Consensus top-X:
                      </label>
                      <input
                        type="number"
                        id="consensus_limit"
                        value={consensusLimit}
                        onChange={(e) => setConsensusLimit(parseInt(e.target.value) || 0)}
                        min="1"
                        step="1"
                        disabled={!showConsensus || (loading && (jobStatus === 'queued' || jobStatus === 'running'))}
                      />
                      <label className="show-consensus-checkbox">
                        <input
                          type="checkbox"
                          checked={showConsensus}
                          onChange={(e) => setShowConsensus(e.target.checked)}
                          disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                        />
                        <span>Show consensus?</span>
                      </label>
                    </div>
                    <div className="rfam-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={rfamEnabled}
                          onChange={(e) => setRfamEnabled(e.target.checked)}
                          disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                        />
                        <span title="Enable the Rfam template search (to be used as structural restraints).">Rfam search</span>
                      </label>
                    </div>
                    <div className="g4-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={g4Enabled}
                          onChange={(e) => setg4Enabled(e.target.checked)}
                          disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                        />
                        <span title="Enable the G-quadruplex pattern recognition (to be used as structural restraints).">G-quadruplex pattern recognition</span>
                      </label>
                    </div>
                    <div className="rbp-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={rbpEnabled}
                          onChange={(e) => setRbpEnabled(e.target.checked)}
                          disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
                        />
                        <span title="Enable the protein-binding motif search (to be used as structural restraints).">Protein-binding motif search</span>
                      </label>
                    </div>
                    <div className="viz-toggle">
                      <label>2D visualization:</label>
                      <button
                        type="button"
                        className={`viz-toggle-button ${vizTool === 'rfview' ? 'active' : ''}`}
                        onClick={() => setVizTool('rfview')}
                      >RFviewJS</button>
                      <button
                        type="button"
                        className={`viz-toggle-button ${vizTool === 'rnacanvas' ? 'active' : ''}`}
                        onClick={() => setVizTool('rnacanvas')}
                      >RNAcanvas</button>
                    </div>
                  </div>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading && (jobStatus === 'queued' || jobStatus === 'running')}
              >
                {loading && (jobStatus === 'queued' || jobStatus === 'running') 
                  ? (jobStatus === 'queued' ? 'In queue...' : 'Processing...') 
                  : 'Predict RNA 2D Structure (SQUARNA)'}
              </button>
            </form>

            {error && (
              <div className="error-message">
                {error.error}
                {error.docs_url && (
                  <>
                    {" "}
                    <a
                      href={error.docs_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ({error.docs_label})
                    </a>
                  </>
                )}
              </div>
            )}

            {predictionData && (
              <div className="result-section">
                <div 
                  className="result-header-row"
                  title="The results section contains the sequence, and the additional input data (if any), followed by the list of ranked (#1, #2, …) predicted structures. The scores of the structures, the quality metrics (to the reference, if any), and the 2D diagrams powered by RFviewJS or RNAcanvas are available under the “?” button (hover or click on it)."
                >
  		  <div className="result-title">Secondary structure</div>
                  <a
   		    href="/help#squarna-results"
  	            className="panel-help-link"
    	            title="Help"
    	            target="_blank"
    		    rel="noopener noreferrer"
  		   >
     		     ?
  		   </a>
		</div>
                <div className="structure-display-block">
                  <div className="prediction-row"> 
                    <div className="prediction-main-block"> 
                      <AutoResizeTextarea
                        className="output-textarea"
                        value={predictionData.input_data}
                        readOnly
                        rows={predictionData.input_data.split('\n').length}
                        onFocus={(e) => e.target.select()}
                        title="The input sequence and structural restraints (if any)"
                      />
                    </div>
                    <div className="prediction-side-info" />
                  </div>

                  {predictionData.predictions.length > 0 ? (
                    predictionData.predictions.map((prediction, index) => {
	              const structureId = prediction.metainfo.split(' ')[0];
                      const formattedTooltip = formatMetainfo(prediction.metainfo);

		      // Fragment returns two elements: row and image block
		      return (
                        <React.Fragment key={index}>
                        <div className="prediction-row">
                          <div className="prediction-main-block">
                            <AutoResizeTextarea
                              className="output-textarea prediction-item-textarea"
                              value={`${prediction.structure}`}
                              readOnly
                              rows="1"
                              onFocus={(e) => e.target.select()}
                              title="Predicted structure in dot-bracket, click the “?” button for details"
                             />
                           </div>
                           <div className="prediction-side-info">
                             <span className="structure-id">{structureId}</span>
                             <span className="info-button-wrap">
                               <button
                                 type="button"
                                 className="info-button"
                                 onMouseEnter={() => handleQHover(prediction.structure, formattedTooltip, index)}
                                 onMouseLeave={() => handleQLeave(index)}
                                 onClick={() => handleQClick(prediction.structure, formattedTooltip, index)}
                                >
                                  ?
                                </button>
                                {blocks[index] && (
                                  <div className="metainfo-popup" onClick={(e) => e.stopPropagation()}>
                                    <AutoResizeTextarea
                                      className="metainfo-popup-textarea"
                                      value={blocks[index].metainfo || ''}
                                      readOnly
                                      rows={1}
                                      maxHeight={400}
                                      onFocus={(e) => e.target.select()}
                                    />
                                  </div>
                                )}
                             </span>
                            </div>
                          </div>
                          {vizTool === 'rnacanvas' && renderImageBlock(index)}
                        </React.Fragment>
                        );
                    })
                  ) : (
                    <p className="no-structures-message">No structures.</p>
                  )}

                  {predictionData.consensus_structure && predictionData.show_consensus && (
                    <>
                      <label className="output-label">Consensus structure (top-{predictionData.cl}):</label>
                      <div className="prediction-row">
                        <div className="prediction-main-block">
                          <AutoResizeTextarea
                            className="output-textarea"
                            value={predictionData.consensus_structure}
                            readOnly
                            rows="1"
                            onFocus={(e) => e.target.select()}
                            title="Predicted consensus structure"
                          />
                        </div>
                        <div className="prediction-side-info">
                          <span className="structure-id"></span>
                          {predictionData.consensus_metainfo && (
                            <span className="info-button-wrap">
                              <button
                                type="button"
                                className="info-button"
                                onMouseEnter={() => handleQHover(predictionData.consensus_structure, predictionData.consensus_metainfo, 'consensus')}
                                onMouseLeave={() => handleQLeave('consensus')}
                                onClick={() => handleQClick(predictionData.consensus_structure, predictionData.consensus_metainfo, 'consensus')}
                              >
                                ?
                              </button>
                              {blocks['consensus'] && (
                                <div className="metainfo-popup" onClick={(e) => e.stopPropagation()}>
                                  <AutoResizeTextarea
                                    className="metainfo-popup-textarea"
                                    value={blocks['consensus'].metainfo || ''}
                                    readOnly
                                    rows={1}
                                    maxHeight={400}
                                    onFocus={(e) => e.target.select()}
                                  />
                                </div>
                              )}
                            </span>
                          )}
                       </div>
                     </div>
                     {vizTool === 'rnacanvas' && renderImageBlock('consensus')}
                   </>
                  )}
              </div>

              {vizTool === 'rfview' && predictionData.predictions.length > 0 && (
                <RFviewCanvas
                  ref={rfviewRef}
                  sequence={predictionData.rna_sequence}
                  predictions={predictionData.predictions}
                  reference={predictionData.reference}
                  reactivitiesStr={predictionData.reactivities}
                  consensusStructure={predictionData.show_consensus ? predictionData.consensus_structure : null}
                />
              )}

              {warnings.length > 0 && (
                <div className="warning-message">
                  <h3>Warnings:</h3>
                  <ul>
                    {warnings.map((warning, index) => (
                      <li key={index}>
                        {warning.message}
                        {warning.docs_url && (
                          <>
                            {" "}
                            <a
                              href={warning.docs_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              ({warning.docs_label})
                            </a>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

        </div>

        <div className="sidebar-controls">
            <AdditionalInputsButtons
              showReactivities={showReactivities}
              setShowReactivities={setShowReactivities}
              showRestraints={showRestraints}
              setShowRestraints={setShowRestraints}
              showReference={showReference}
              setShowReference={setShowReference}
              showAlignment={showAlignment}
              setShowAlignment={setShowAlignment}
              showParameters={showParameters}
              setShowParameters={setShowParameters}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Navbar() {
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  return (
    <nav className="navbar">
      <div className="navbar-left">
        <a href="/" className="navbar-brand">LaRNAl Platform</a>
        <a href="/about" className="navbar-link">About</a>
        <a href="/help" className="navbar-link">Help</a>
        <div
          className="navbar-dropdown-wrapper"
          onClick={() => setStandaloneOpen(!standaloneOpen)}
          onMouseLeave={() => setStandaloneOpen(false)}
        >
          <span className={`navbar-link navbar-dropdown-trigger ${standaloneOpen ? 'active' : ''}`}>
            Standalone
          </span>
          {standaloneOpen && (
            <div className="navbar-dropdown">
              <div className="navbar-dropdown-item-wrapper">
                <span className="navbar-dropdown-item">SQUARNA</span>
                <div className="navbar-subdropdown">
                  <a href="https://pypi.org/project/SQUARNA/" target="_blank" rel="noopener noreferrer" className="navbar-subdropdown-item">PyPI</a>
                  <a href="https://github.com/febos/SQUARNA/tree/main" target="_blank" rel="noopener noreferrer" className="navbar-subdropdown-item">GitHub</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="navbar-right">
        <a href="/contact" className="navbar-link">Contact</a>
        <a href="https://imol.institute/" target="_blank" rel="noopener noreferrer" className="navbar-external">
          <img src="/logos/imol.png" alt="IMol" className="navbar-logo" />
        </a>
        <a href="https://projekty.ncn.gov.pl/en/index.php?jednostka=&jednostka_miasto=&jednostka_wojewodztwo=&kierownik=Baulin&kierownik_plec=&kierownik_tytul=&status=&projekt=&kwotaprzyznanaod=&kwotaprzyznanado=&typkonkursu=&konkurs=&grupa=&panel=&slowokluczowe=&aparatura=" target="_blank" rel="noopener noreferrer" className="navbar-external">
          <img src="/logos/ncn.png" alt="NCN" className="navbar-logo" />
        </a>
        <a href="https://imol.institute/leaders/baulin-group/" target="_blank" rel="noopener noreferrer" className="navbar-external">
          <span>Baulin Lab</span>
        </a>
      </div>
    </nav>
  );
}

function AboutPage() {
  return (
    <div className="static-page">
      <h1>About</h1>

      <p>
        The <strong>LaRNAl Platform</strong> allows users to perform RNA structure analysis,
        modeling, and prediction tasks using (primarily) tools developed by the{' '}
        <a href="https://imol.institute/leaders/baulin-group/" target="_blank" rel="noopener noreferrer">
          <strong>La</strong>boratory of <strong>RN</strong>A <strong>Al</strong>gorithms at IMol PAS
        </a>
        . Currently, it provides a web-based implementation of SQUARNA, our tool for RNA secondary
        structure prediction. Its web version was designed for the analysis of individual RNA
        sequences, with optional auxiliary input such as sequence alignments, structural restraints,
        reference structures, and residue reactivities, as well as automated searches for Rfam
        templates, G-quadruplexes, and protein-binding motifs. The LaRNAl Platform will soon feature
        R3FOLD (RNA 3D structure modeling and prediction), ALTERNA (RNA sequence alignment), ARTEMANN
        (RNA 3D structure annotation), and R3BIND (RNA-protein interaction prediction).
      </p>

      <h2>References</h2>
      <ol>
        <li>M.D. Serdakov, D.R. Bohdan, G.I. Nikolaev, J.M. Bujnicki, E.F. Baulin (2026) Revisiting the base pair maximization approach for RNA secondary structure prediction with SQUARNA. bioRxiv. DOI: <a href="https://doi.org/10.64898/2026.06.30.735492" target="_blank" rel="noopener noreferrer">
          10.64898/2026.06.30.735492
        </a></li>
      </ol>

      <h2>Acknowledgements and Funding</h2>
      <p>
        The project is supported by the National Science Centre, Poland [grants
        2024/53/B/NZ2/02718 and 2024/55/D/NZ2/02049 to Eugene F. Baulin]. The system is hosted
        and maintained by the{' '}
        <a href="https://imol.institute/" target="_blank" rel="noopener noreferrer">
          International Institute of Molecular Mechanisms and Machines, Polish Academy of Sciences
        </a>
        .
      </p>

      <h2>Privacy Policy</h2>
      <p>
        This website is free and open to all users and there is no login required. We process
        limited technical data necessary to operate and secure this website, including IP addresses,
        request timestamps, and server response information. This data is used solely for security,
        debugging, abuse prevention, and aggregated service statistics under our legitimate interest
        pursuant to Article 6(1)(f) GDPR. Raw server logs are automatically deleted after 30 days,
        and only anonymized aggregate statistics are retained long term. This website does not use
        advertising cookies or third-party tracking analytics.
      </p>
    </div>
  );
}

function HelpPage() {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'auto' }), 0);
      }
    }
  }, []);

  return (
    <div className="static-page">
      <h1>Help</h1>

      <nav>
        <ol>
          <li><a href="#general-information">General information</a></li>
          <li>
            <a href="#squarna">2D structure (SQUARNA)</a>
            <ul>
              <li><a href="#example1">Example 1: tRNA sequence, default mode</a></li>
              <li><a href="#example2">Example 2: Telomerase pseudoknot with residue reactivities</a></li>
              <li><a href="#example3">Example 3: HDV ribozyme with sequence alignment and reference structure</a></li>
              <li><a href="#example4">Example 4: G-quadruplex &amp; protein-binding motif</a></li>
              <li><a href="#example5">Example 5: Two RNA chains with restraints</a></li>
            </ul>
          </li>
          <li>
            <a href="#manual">Manual</a>
            <ul>
              <li><a href="#input-page">Input page</a></li>
              <li><a href="#squarna-results">2D structure (SQUARNA) results page</a></li>
            </ul>
          </li>
        </ol>
      </nav>

      <h2 id="general-information">General information</h2>
      <p>
        The <strong>LaRNAl Platform</strong> allows users to perform RNA structure analysis,
        modeling, and prediction tasks using (primarily) tools developed by the{' '}
        <a href="https://imol.institute/leaders/baulin-group/" target="_blank" rel="noopener noreferrer">
          <strong>La</strong>boratory of <strong>RN</strong>A <strong>Al</strong>gorithms at IMol PAS
        </a>
        . Currently, it provides a web-based implementation of SQUARNA, our tool for RNA secondary
        structure prediction. Its web version was designed for the analysis of individual RNA
        sequences, with optional auxiliary input such as sequence alignments, structural restraints,
        reference structures, and residue reactivities, as well as automated searches for Rfam
        templates, G-quadruplexes, and protein-binding motifs. The LaRNAl Platform will soon feature
        R3FOLD (RNA 3D structure modeling and prediction), ALTERNA (RNA sequence alignment), ARTEMANN
        (RNA 3D structure annotation), and R3BIND (RNA-protein interaction prediction).
      </p>

      <h2 id="squarna">2D structure (SQUARNA)</h2>

      <h3 id="example1"><a href="/jobs/example1" className="example-job-link" target="_blank" rel="noopener noreferrer">Example 1: tRNA sequence, default mode</a></h3>
      <p>
        A tRNA sequence with no additional input and all the default parameters. The Rfam template
        search identifies the match with the tRNA family, and SQUARNA predicts a single structure
        that fully matches the restraints from the template.
      </p>
      <img src="/help/example_1.png" alt="Example 1 input and predicted secondary structure" className="help-screenshot" />

      <h3 id="example2"><a href="/jobs/example2" className="example-job-link" target="_blank" rel="noopener noreferrer">Example 2: Telomerase pseudoknot with residue reactivities</a></h3>
      <p>
        The telomerase pseudoknot sequence from the S01 dataset of ShapeKnots (<a href="https://doi.org/10.1073/pnas.1219988110" target="_blank" rel="noopener noreferrer">https://doi.org/10.1073/pnas.1219988110</a>) with SHAPE data. The Rfam template search identifies the match
        with the Telomerase-vert family, resulting in single-stem structural restraints. The results section features the encoded line
        of residue reactivities for easy navigation, with “#” for reactive residues, “_” for non-reactive residues, and “+” for intermediate values. 
        SQUARNA predicts three structures, with the correct H-knot matching the #2 structure, while the #1 structure features an additional pseudoknotted stem resulting in a third-order pseudoknot.       
      </p>
      <img src="/help/example_2.png" alt="Example 2 input and predicted secondary structure" className="help-screenshot" />

      <h3 id="example3"><a href="/jobs/example3" className="example-job-link" target="_blank" rel="noopener noreferrer">Example 3: HDV ribozyme with sequence alignment and reference structure</a></h3>
      <p>
        An HDV ribozyme with a reference 2D structure and a sequence alignment in FASTA format. The alignment features the input sequence as the first sequence in a gapped form. 
        With alignment input, SQUARNA runs in the alignment mode, with all restraints searches disabled. The results section shows the input sequence in the gapped form, as in the alignment, 
        the reference 2D structure mapped to this gapped form, as well as the predicted structure. The alignment-mode SQUARNA always predicts a single individual structure for the input sequence, 
        which accounts for the input alignment. The metadata for the predicted structure (under the “?” button) includes the quality metrics relative to the reference.
      </p>
      <img src="/help/example_3.png" alt="Example 3 input and predicted secondary structure" className="help-screenshot" />

      <h3 id="example4"><a href="/jobs/example4" className="example-job-link" target="_blank" rel="noopener noreferrer">Example 4: G-quadruplex &amp; protein-binding motif</a></h3>
      <p>
        An illustrative example sequence showcasing the G-quadruplex and protein-binding motif search. The Rfam template search reports no hits here, 
        whereas SQUARNA identifies a single G-quadruplex motif and a Fab-binding motif GAAACAC. Based on the matches, SQUARNA forces the G residues of the quadruplex 
        to be unpaired (“+” symbols) and the flanking residues of the Fab motif to form a base pair. As a result, SQUARNA predicts a single two-stem structure that satisfies the restraints. 
      </p>
      <img src="/help/example_4.png" alt="Example 4 input and predicted secondary structure" className="help-screenshot" />

      <h3 id="example5"><a href="/jobs/example5" className="example-job-link" target="_blank" rel="noopener noreferrer">Example 5: Two RNA chains with restraints</a></h3>
      <p>
        An illustrative example showcasing multiple-chain input with structural restraints. A semicolon separates the two input chains. The restraints include a single stretch of residues 
        allowed to form base pairs only “to the right” (“/” symbols), i.e., to the 3’-end. SQUARNA predicts five alternative structures: in three, the restrained stretch is base-paired, and in two, 
        it is unpaired, all of which satisfy the restraints.
      </p>
      <img src="/help/example_5.png" alt="Example 5 input and predicted secondary structure" className="help-screenshot" />

      <h2 id="manual">Manual</h2>

      <h3 id="input-page">Input page</h3>
      <p>
        The input consists of three sections: the header, the input section, and the footer. The header lists standard additional tabs (About, Help, Contact), an expandable list of standalone versions of the tools (Standalone), 
        as well as links to the IMol website, the list of related NCN funding, and the LaRNAl page (Baulin Lab). 
      </p>

      <h4 id="examples">Examples</h4>
      <p>
        The Examples block of the input section features Example input buttons. Clicking on the buttons fills the corresponding example inputs, and clicking on the “?” button opens the corresponding section of the Help page.
      </p>

      <h4 id="rna-sequence">RNA sequence</h4>
      <p>
        RNA sequence up to 500 nts, in FASTA format or as plain text. Symbols 'AaCcGgUu' will be treated as the four types of RNA bases in a case-insensitive manner. Symbols 'Tt' will be replaced with 'U'. 
        Symbols ';&' will be treated as the separators of different RNA chains. Symbols '.-~' will be treated as gaps. Spacing symbols will be removed. All the other symbols will be treated as bases that cannot form base pairs.
      </p>

      <h4 id="reactivities">Reactivities</h4>
      <p>
        Residue reactivities either as a space/tab separated list of float values (with the number of values equal to the sequence length, including the separator positions whose values will be ignored) or as 
        a two-column table of space/tab separated value pairs, where the first is a 1-based position index and the second is its reactivity value (missing values will be filled with -999). 
        Values of -10 and lower will be treated as missing values. Other float values will be clipped to the 0.0-1.0 range using min(max(x,0),1). 
      </p>

      <h4 id="restraints">Restraints</h4>
      <p>
        Restraints line matching the sequence length. All pairs of brackets, (), [], {}, &lt;&gt;, and pairs of Latin letters (Aa,Bb,...Yy,Zz) will be treated as base pairs. Symbols '_' and ‘+’ will be treated as unpaired bases. 
        The slash sign '/' will be treated as the base that cannot form any base pairs 'to the left' (i.e. with the counterpart being closer to the 5'-end). In contrast, the backslash sign '\' will be treated as the base 
        that cannot form any base pairs 'to the right' (i.e. with the counterpart being closer to the 3'-end). All the other symbols will be treated as unrestrained positions.
      </p>

      <h4 id="reference">Reference</h4>
      <p>
        Reference 2D structure in dot-bracket format, matching the sequence length. Pairs of brackets, (),[],{},&lt;&gt;, and pairs of Latin letters (Aa,Bb,...Yy,Zz) will be treated as base pairs.
      </p>

      <h4 id="alignment">Alignment</h4>
      <p>
        Multiple sequence alignment in FASTA, Stockholm, or Clustal (.aln) format. The alignment must feature the input sequence, possibly with gaps.
      </p>

      <h4 id="parameters">Parameters</h4>
      <p>
        <strong>#Structures.</strong> Number of predicted structures to report.
      </p>
      <p>
        <strong>Consensus top-X.</strong> Value X for the consensus of the top-X structures.
      </p>
      <p>
        <strong>Rfam search.</strong> The Rfam family search uses the cmscan utility from the Infernal package
        (<a href="http://eddylab.org/infernal/" target="_blank" rel="noopener noreferrer">http://eddylab.org/infernal/</a>), with the options “-E 1e-4” (E-value threshold), “--rfam” (Rfam-like score
        thresholds), and “--toponly” (ignore reverse strand). The cmscan-predicted structures are subsequently
        mapped onto the input sequence in decreasing order of significance, while ignoring inconsistent base pairs.
      </p>
      <p>
        <strong>G-quadruplex pattern recognition.</strong> For G-quadruplexes, SQUARNA uses a regular expression
        search for two patterns, G<sub>2-5</sub>N<sub>1-2</sub>G<sub>2-5</sub>N<sub>1-2</sub>G<sub>2-5</sub>N<sub>1-2</sub>G<sub>2-5</sub> and
        G<sub>3-5</sub>N<sub>1-12</sub>G<sub>3-5</sub>N<sub>1-12</sub>G<sub>3-5</sub>N<sub>1-12</sub>G<sub>3-5</sub>, followed by
        filtering using a G4Hunter score threshold of 1.2 (<a href="https://doi.org/10.1093/nar/gkw006" target="_blank" rel="noopener noreferrer">https://doi.org/10.1093/nar/gkw006</a>).
      </p>
      <p>
        <strong>Protein-binding motif search.</strong> SQUARNA identifies the following RBP-binding motifs:
        AUUGCAC (all unpaired, U1A), GAAACAC (GC base pair flanking a loop of unpaired residues, Fab), GGAGA (all
        unpaired, LIN28), UGCAUG (all unpaired, RBFOX1/2), UGUAHAUW (H = A/C/U, W = A/U, all unpaired, PUM), and
        ACUAAC (all unpaired, SF1/QKI).
      </p>

      <h4 id="submit-buttons">Submit buttons</h4>
      <p>
        The submit buttons run the corresponding predictions/annotations. Currently, only the 2D structure prediction using SQUARNA is available; more to come soon.
      </p>

      <h3 id="squarna-results">2D structure (SQUARNA) results page</h3>
      <p>
        The results page shows the Job ID section (job ID + status), the adjustable input section, and the results section. Each job is stored for 30 days and then gets removed from the server. 
        Jobs have persistent URLs, e.g., <a href="https://larnal.imol.institute/jobs/example1" target="_blank" rel="noopener noreferrer">https://larnal.imol.institute/jobs/example1</a>. 

        The results section contains the sequence, and the additional input data (if any), followed by the list of ranked (#1, #2, …) predicted structures. The scores of the structures, the quality metrics
        (to the reference, if any), and the 2D diagrams powered by{' '}
        <a href="https://github.com/dincarnato/RFviewJS" target="_blank" rel="noopener noreferrer">RFviewJS</a>{' '}
        or{' '}
        <a href="https://doi.org/10.1093/nar/gkad302" target="_blank" rel="noopener noreferrer">RNAcanvas</a>{' '}
        are available under the “?” button (hover or click on it). The quality metrics include: true positives (TP), false positives (FP), false negatives (FN),
        F-score (FS), precision (PR), recall (RC), and the rank of the structure closest to the reference (RK). The scores include the structure score (see the SQUARNA manuscript), the reactivity score (MAE), and the product of the two scores.
      </p>
    </div>
  );
}

function ContactPage() {
  return (
    <div className="static-page">
      <h1>Contact</h1>

      <p>
        <a href="https://imol.institute/leaders/baulin-group/" target="_blank" rel="noopener noreferrer">
          Laboratory of RNA Algorithms (LaRNAl)
        </a>
      </p>
      <p>
        <a href="https://imol.institute/" target="_blank" rel="noopener noreferrer">
          The International Institute of Molecular Mechanisms and Machines, Polish Academy of Sciences
        </a>
      </p>
      <p>
        ul. M. Flisa 6<br />
        02-247 Warsaw, Poland
      </p>
      <p>e.baulin([at)]imol.institute (Eugene F. Baulin)</p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <p>This website is free and open to all users and there is no login required.</p>
      <p>© 2026 Laboratory of RNA Algorithms, IMol Polish Academy of Sciences</p>
    </footer>
  );
}

// Routing component
function App() {
  return (
    <Router>
      <Navbar />
      <Routes>
        <Route path="/" element={<SquarnaApp />} />
        <Route path="/jobs/:jobId" element={<SquarnaApp />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
      <Footer />
    </Router>
  );
}

export default App;
